/** Native actor transport contracts; no prices, grants or approval are derived. */
import { httpClient } from "./client";
import type {
  ContractTermsResponse,
  OrgContractVersionOwnerView,
  OrgContractAcceptanceOwnerView,
  CommercialFeeDraftPage,
  CommercialFeeDraftReplace,
  CommercialFeePolicyDraft,
  CommercialFeePolicyWrite,
  CommercialFeePolicyVersion,
  CommercialFeeVersionPage,
  CommercialFeePublicationReview,
  CommercialPublicationRequest,
  CommercialCapabilityPage,
} from "../types/generated";

export interface OwnerContractApi {
  getTerms(orgId: string): Promise<ContractTermsResponse>;
  listVersions(
    orgId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<OrgContractVersionOwnerView[]>;
  listAcceptances(
    orgId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<OrgContractAcceptanceOwnerView[]>;
}

export interface CommercialFeeAuthoringApi {
  listDrafts(params?: {
    limit?: number;
    offset?: number;
  }): Promise<CommercialFeeDraftPage>;
  createDraft(
    data: CommercialFeePolicyWrite,
  ): Promise<CommercialFeePolicyDraft>;
  getDraft(draftId: string): Promise<CommercialFeePolicyDraft>;
  replaceDraft(
    draftId: string,
    data: CommercialFeeDraftReplace,
  ): Promise<CommercialFeePolicyDraft>;
  getVersion(versionId: string): Promise<CommercialFeePolicyVersion>;
  getPublicationReview(
    draftId: string,
    environment: CommercialPublicationRequest["environment"],
  ): Promise<CommercialFeePublicationReview>;
  publish(
    draftId: string,
    data: CommercialPublicationRequest,
  ): Promise<CommercialFeePolicyVersion>;
  listVersions(params: {
    audience_org_id: string;
    limit?: number;
    offset?: number;
  }): Promise<CommercialFeeVersionPage>;
}

function page(params: { limit?: number; offset?: number } = {}): string {
  const query = new URLSearchParams();
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  if (params.offset !== undefined) query.set("offset", String(params.offset));
  return query.size ? `?${query}` : "";
}

export const ownerContractApi: OwnerContractApi = {
  getTerms: (orgId) =>
    httpClient.get(`/orgs/${encodeURIComponent(orgId)}/contract-terms`),
  listVersions: (orgId, params) =>
    httpClient.get(
      `/orgs/${encodeURIComponent(orgId)}/contract-versions${page(params)}`,
    ),
  listAcceptances: (orgId, params) =>
    httpClient.get(
      `/orgs/${encodeURIComponent(orgId)}/contract-acceptances${page(params)}`,
    ),
};

export const commercialFeeAuthoringApi: CommercialFeeAuthoringApi = {
  getPublicationReview: (draftId, environment) =>
    httpClient.get(
      `/platform/commercial/fee-policies/drafts/${encodeURIComponent(draftId)}/publication-review?environment=${encodeURIComponent(environment)}`,
    ),
  publish: (draftId, data) =>
    httpClient.post(
      `/platform/commercial/fee-policies/drafts/${encodeURIComponent(draftId)}/publish`,
      data,
    ),
  listVersions: (params) => {
    const query = new URLSearchParams({ audience_org_id: params.audience_org_id });
    if (params.limit !== undefined) query.set("limit", String(params.limit));
    if (params.offset !== undefined) query.set("offset", String(params.offset));
    return httpClient.get(`/platform/commercial/fee-policies/versions?${query.toString()}`);
  },
  listDrafts: (params) =>
    httpClient.get(`/platform/commercial/fee-policies/drafts${page(params)}`),
  createDraft: (data) =>
    httpClient.post("/platform/commercial/fee-policies/drafts", data),
  getDraft: (id) =>
    httpClient.get(
      `/platform/commercial/fee-policies/drafts/${encodeURIComponent(id)}`,
    ),
  replaceDraft: (id, data) =>
    httpClient.put(
      `/platform/commercial/fee-policies/drafts/${encodeURIComponent(id)}`,
      data,
    ),
  getVersion: (id) =>
    httpClient.get(
      `/platform/commercial/fee-policies/versions/${encodeURIComponent(id)}`,
    ),
};

export const commercialCapabilitiesApi = {
  get: (
    orgId: string,
    facilityId?: string,
  ): Promise<CommercialCapabilityPage> =>
    httpClient.get(
      `/orgs/${encodeURIComponent(orgId)}/commercial/capabilities${facilityId ? `?facility_id=${encodeURIComponent(facilityId)}` : ""}`,
    ),
};
